/**
 * Copyright 2024 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

// NOTE: Not being used for playing audio, Instead GlobalAudioPlayer is used in use-live-api.ts
export class AudioStreamer {
  public audioQueue: Float32Array[] = [];
  private isPlaying: boolean = false;
  private sampleRate: number;
  private bufferSize: number = 2400; // 100ms at 24kHz for more stable buffering
  private processingBuffer: Float32Array = new Float32Array(0);
  private scheduledTime: number = 0;
  public gainNode: GainNode;
  public source: AudioBufferSourceNode;
  private isStreamComplete: boolean = false;
  private checkInterval: number | null = null;
  private initialBufferTime: number = 0.15; // 150ms initial buffer for smoother playback
  private endOfQueueAudioSource: AudioBufferSourceNode | null = null;
  public onComplete = () => {};
  public sourceSampleRate: number = 24000; // Default to 24kHz, adjust if needed
  private lastProcessTime: number = 0; // Track last processing time for rate limiting
  private minQueueSize: number = 2; // Wait for 2 chunks before starting for smoother playback

  constructor(public context: AudioContext) {
    this.sampleRate = this.context.sampleRate;
    this.gainNode = this.context.createGain();
    this.source = this.context.createBufferSource();
    this.gainNode.connect(this.context.destination);
    this.addPCM16 = this.addPCM16.bind(this);
    this.gainNode.gain.value = 1;
    console.log('[AudioStreamer] Initialized with sample rate:', this.sampleRate);
  }

  // Linear resampling from srcRate to dstRate
  private resampleBuffer(input: Float32Array, srcRate: number, dstRate: number): Float32Array {
    if (srcRate === dstRate) return input;
    const ratio = dstRate / srcRate;
    const newLength = Math.round(input.length * ratio);
    const output = new Float32Array(newLength);
    for (let i = 0; i < newLength; i++) {
      const srcIndex = i / ratio;
      const low = Math.floor(srcIndex);
      const high = Math.min(Math.ceil(srcIndex), input.length - 1);
      const weight = srcIndex - low;
      output[i] = input[low] * (1 - weight) + input[high] * weight;
    }
    return output;
  }

  async addPCM16(chunk: Uint8Array) {
    console.log('[AudioStreamer] addPCM16 called, chunk length:', chunk.length);
    
    if (this.context.state === 'suspended') {
      try {
        await this.context.resume();
        console.log('[AudioStreamer] AudioContext resumed');
      } catch (e) {
        console.warn('[AudioStreamer] Failed to resume AudioContext:', e);
      }
    }
    const float32Array = new Float32Array(chunk.length / 2);
    const dataView = new DataView(chunk.buffer);
    for (let i = 0; i < chunk.length / 2; i++) {
      try {
        const int16 = dataView.getInt16(i * 2, true);
        float32Array[i] = int16 / 32768;
      } catch (e) {
        console.error(e);
      }
    }
    const newBuffer = new Float32Array(
      this.processingBuffer.length + float32Array.length,
    );
    newBuffer.set(this.processingBuffer);
    newBuffer.set(float32Array, this.processingBuffer.length);
    this.processingBuffer = newBuffer;
    while (this.processingBuffer.length >= this.bufferSize) {
      const buffer = this.processingBuffer.slice(0, this.bufferSize);
      this.audioQueue.push(buffer);
      this.processingBuffer = this.processingBuffer.slice(this.bufferSize);
      console.log('[AudioStreamer] Queued buffer, queue length:', this.audioQueue.length);
    }
    if (!this.isPlaying) {
      this.isPlaying = true;
      this.scheduledTime = this.context.currentTime + this.initialBufferTime;
    }
    this.scheduleNextBuffer();
  }

  private createAudioBuffer(audioData: Float32Array): AudioBuffer {
    let processed = audioData;
    if (this.sourceSampleRate !== this.sampleRate) {
      processed = this.resampleBuffer(audioData, this.sourceSampleRate, this.sampleRate);
      console.log(`[AudioStreamer] Resampled buffer from ${this.sourceSampleRate}Hz to ${this.sampleRate}Hz, new length: ${processed.length}`);
    }
    const audioBuffer = this.context.createBuffer(
      1,
      processed.length,
      this.sampleRate,
    );
    audioBuffer.getChannelData(0).set(processed);
    return audioBuffer;
  }

  private scheduleNextBuffer() {
    const SCHEDULE_AHEAD_TIME = 0.3; // Increased for better buffering
    let scheduledAny = false;
    
    // Wait for minimum queue size before starting playback
    if (!this.isPlaying && this.audioQueue.length < this.minQueueSize) {
      // Check again soon if we're waiting for buffers
      if (this.audioQueue.length > 0) {
        setTimeout(() => this.scheduleNextBuffer(), 10);
      }
      return;
    }
    
    while (
      this.audioQueue.length > 0 &&
      this.scheduledTime < this.context.currentTime + SCHEDULE_AHEAD_TIME
    ) {
      const audioData = this.audioQueue.shift()!;
      const audioBuffer = this.createAudioBuffer(audioData);
      const source = this.context.createBufferSource();
      
      // Add a small fade-in to reduce clicks
      source.buffer = audioBuffer;
      source.connect(this.gainNode);
      
      if (this.audioQueue.length === 0) {
        if (this.endOfQueueAudioSource) {
          this.endOfQueueAudioSource.onended = null;
        }
        this.endOfQueueAudioSource = source;
        source.onended = () => {
          if (!this.audioQueue.length && this.endOfQueueAudioSource === source) {
            this.endOfQueueAudioSource = null;
            this.onComplete();
            this.isPlaying = false;
            this.audioQueue = [];
            this.processingBuffer = new Float32Array(0);
          }
          // Always try to schedule the next buffer after one ends
          this.scheduleNextBuffer();
        };
      } else {
        // For all other buffers, also chain scheduleNextBuffer
        source.onended = () => {
          this.scheduleNextBuffer();
        };
      }
      
      const startTime = Math.max(this.scheduledTime, this.context.currentTime);
      
      // Add crossfade for smoother transitions
      const fadeTime = 0.01; // 10ms crossfade
      const adjustedStartTime = this.isPlaying ? startTime - fadeTime : startTime;
      
      // Apply fade in/out for smoother transitions
      const gainNode = this.context.createGain();
      source.connect(gainNode);
      gainNode.connect(this.gainNode);
      
      if (this.isPlaying && scheduledAny) {
        // Fade in
        gainNode.gain.setValueAtTime(0, adjustedStartTime);
        gainNode.gain.linearRampToValueAtTime(1, adjustedStartTime + fadeTime);
      }
      
      try {
        source.start(adjustedStartTime);
      } catch (e) {
        console.error('[AudioStreamer] Error starting source:', e);
      }
      this.scheduledTime = startTime + audioBuffer.duration - fadeTime;
      scheduledAny = true;
    }
    // If we scheduled at least one buffer, ensure isPlaying is true
    if (scheduledAny) {
      this.isPlaying = true;
    }
    // If the queue is not empty and not playing, try to resume
    if (this.audioQueue.length > 0 && !this.isPlaying) {
      this.isPlaying = true;
      this.scheduledTime = this.context.currentTime + this.initialBufferTime;
      this.scheduleNextBuffer();
    }
    if (this.audioQueue.length === 0 && this.processingBuffer.length === 0) {
      if (this.isStreamComplete) {
        this.isPlaying = false;
        if (this.checkInterval) {
          clearInterval(this.checkInterval);
          this.checkInterval = null;
        }
      } else {
        if (!this.checkInterval) {
          this.checkInterval = window.setInterval(() => {
            if (
              this.audioQueue.length > 0 ||
              this.processingBuffer.length >= this.bufferSize
            ) {
              this.scheduleNextBuffer();
            }
          }, 100) as unknown as number;
        }
      }
    }
  }

  stop() {
    this.isPlaying = false;
    this.audioQueue = [];
    this.processingBuffer = new Float32Array(0);
    if (this.checkInterval) {
      clearInterval(this.checkInterval);
      this.checkInterval = null;
    }
    this.gainNode.disconnect();
    this.gainNode = this.context.createGain();
    this.gainNode.connect(this.context.destination);
    this.gainNode.gain.value = 1;
    console.log('[AudioStreamer] Stopped and reset gain node');
  }

  async resume() {
    if (this.context.state === "suspended") {
      await this.context.resume();
      console.log('[AudioStreamer] AudioContext resumed in resume()');
    }
    this.isStreamComplete = false;
    this.scheduledTime = this.context.currentTime + this.initialBufferTime;
    this.gainNode.gain.setValueAtTime(1, this.context.currentTime);
    console.log('[AudioStreamer] resume() called, gain set to 1');
  }

  complete() {
    this.isStreamComplete = true;
    // Always push any remaining buffer data
    if (this.processingBuffer.length > 0) {
      // Pad the last buffer if it's too small
      if (this.processingBuffer.length < this.bufferSize / 2) {
        const paddedBuffer = new Float32Array(this.bufferSize);
        paddedBuffer.set(this.processingBuffer);
        this.audioQueue.push(paddedBuffer);
      } else {
        this.audioQueue.push(this.processingBuffer);
      }
      this.processingBuffer = new Float32Array(0);
    }
    
    // Ensure we schedule playback if there's any audio left
    if (this.audioQueue.length > 0) {
      this.scheduleNextBuffer();
    } else if (!this.isPlaying) {
      this.onComplete();
    }
  }

  // Debug: Play a test tone
  playTestTone() {
    const duration = 0.5;
    const oscillator = this.context.createOscillator();
    oscillator.type = 'sine';
    oscillator.frequency.value = 440;
    oscillator.connect(this.gainNode);
    oscillator.start();
    oscillator.stop(this.context.currentTime + duration);
    console.log('[AudioStreamer] Playing test tone');
  }
}

// // Usage example:
// const audioStreamer = new AudioStreamer();
//
// // In your streaming code:
// function handleChunk(chunk: Uint8Array) {
//   audioStreamer.handleChunk(chunk);
// }
//
// // To start playing (call this in response to a user interaction)
// await audioStreamer.resume();
//
// // To stop playing
// // audioStreamer.stop();
